// import { Banner } from '@trackman/baseball-shared-components';
import { Banner } from '@trackman/baseball-shared-components';
import { Toast } from '@trackman/web-shared-components';
import { UploadWarning } from 'components/ui/UploadWarning/UploadWarning';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect } from 'react';
import { BrowserRouter as _Router, BrowserRouterProps, Route } from 'react-router-dom';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { NotificationStore } from 'stores/NotificationStore';
import { BannerWrapper } from 'styles/LayoutStyles';
import NavBar from './components/header/NavBar/NavBar';
import FaqModal from './components/ui/FaqModal/FaqModal';
import { SyncBar } from './components/ui/SyncBar/SyncBar';
import { ServiceTypes } from './constants/ServiceTypes';
import Archives from './pages/Archives/Archives';
import BpSessionReview from './pages/BattingPractice/Review/BpSessionReview';
import BpSetLineup from './pages/BattingPractice/SetLineup/BpSetLineup';
import BpTagging from './pages/BattingPractice/Tagging/BpTagging';
import BpScoreBar from './pages/BattingPractice/Tagging/components/ScoreBar/BpScoreBar';
import FieldCalibration from './pages/FieldCalibration/FieldCalibration';
import Home from './pages/Home/Home';
import SessionReview from './pages/Review/SessionReview';
import ScheduledGames from './pages/ScheduledGames/ScheduledGames';
import SetLineup from './pages/SetLineup/SetLineup';
import ScoreBar from './pages/Tagging/components/ScoreBar/ScoreBar';
import Tagging from './pages/Tagging/Tagging';
import { useInjection } from './services/ioc/ioc.react';
import { FieldCalibrationStore } from './stores/FieldCalibrationStore';
import { GameReviewStore } from './stores/GameReviewStore';
import { BpTaggingStore } from './stores/Tagging/BpTaggingStore';
import { TaggingStore } from './stores/Tagging/TaggingStore';
import { UiStore } from './stores/UiStore';

const Router = _Router as unknown as FC<BrowserRouterProps & { children: React.ReactNode }>;

const Layout: React.FC = observer(() => {
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const bpTaggingStore = useInjection<BpTaggingStore>(ServiceTypes.BpTaggingStore);
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const reviewStore = useInjection<GameReviewStore>(ServiceTypes.GameReviewStore);
  const fieldCalibrationStore = useInjection<FieldCalibrationStore>(ServiceTypes.FieldCalibrationStore);
  const basename = process.env.NODE_ENV === 'production' ? '/taggingapp' : '';
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  useEffect(() => {
    contextualDataStore.refreshCurrentDayWithoutSync();
  }, []);

  const showSyncBar = (): boolean => {
    return (
      !taggingStore.isTagging &&
      !reviewStore.isReviewing &&
      !uiStore.isSettingLineup &&
      !fieldCalibrationStore.isFieldCalibration &&
      !bpTaggingStore.isTagging
    );
  };

  const showScoreBar = (): boolean => {
    return taggingStore.isTagging && taggingStore.isInitialized;
  };

  const showBpScoreBar = (): boolean => {
    return bpTaggingStore.isTagging && bpTaggingStore.isInitialized;
  };

  useEffect(() => {
    if (uiStore.isMoreThanOneSession && !reviewStore.isReviewing) {
      notificationStore.addNotification({
        id: 'multiple-sessions-warning',
        message: 'More than 1 instance of this game is running, this can lead to corrupted data.',
        variant: 'error',
      });
    }

    if (!uiStore.isMoreThanOneSession) {
      notificationStore.removeNotification('multiple-sessions-warning');
    }
  }, [uiStore.isMoreThanOneSession, reviewStore.isReviewing]);

  useEffect(() => {
    if (!uiStore.isVideoUploaded && showSyncBar()) {
      notificationStore.addNotification({
        id: 'video-upload-warning',
        message: <UploadWarning />,
        variant: 'warning',
      });
    }

    if (uiStore.isVideoUploaded) {
      notificationStore.removeNotification('video-upload-warning');
    }
  }, [
    uiStore.isVideoUploaded,
    taggingStore.isTagging,
    reviewStore.isReviewing,
    uiStore.isSettingLineup,
    fieldCalibrationStore.isFieldCalibration,
    bpTaggingStore.isTagging,
  ]);

  const getBanners = () => {
    // window.location.href instead of useLocation because Router is rendered after
    const url = window.location.href;
    if (
      taggingStore.isTagging ||
      reviewStore.isReviewing ||
      uiStore.isSettingLineup ||
      fieldCalibrationStore.isFieldCalibration ||
      bpTaggingStore.isTagging ||
      url.includes('/games')
    ) {
      return notificationStore.notifications.filter((notification) => notification.variant !== 'software-update');
    }

    return notificationStore.notifications;
  };

  return (
    <Router basename={basename}>
      <div className='App'>
        {!reviewStore.isReviewing && <NavBar />}
        <Toast
          open={uiStore.isSystemUpdateCompleted}
          onClose={() => uiStore.setIsSystemUpdateCompleted(undefined)}
          severity='success'
          header='Update completed'
          message='The system has been successfully updated.'
        />
        <Toast
          open={uiStore.isPTUUpdateCompleted}
          onClose={() => uiStore.setIsPTUUpdateCompleted(undefined)}
          severity='success'
          header='Update completed'
          message='The tracking elements have been successfully updated.'
        />
        <BannerWrapper>
          <Banner banners={getBanners()} />
        </BannerWrapper>
        {notificationStore.toast && <Toast {...notificationStore.toast} />}
        <div className={!reviewStore.isReviewing ? 'container' : ''} style={!reviewStore.isReviewing ? { marginTop: '50px' } : {}}>
          <Route path='/' exact component={Home} />
          <Route path='/set-lineup/' component={SetLineup} />
          <Route path='/tagging/' component={Tagging} />
          <Route path='/games/' component={ScheduledGames} />
          <Route path='/review/:id' component={SessionReview} />
          <Route path='/archives/' component={Archives} />
          <Route path='/set-lineup-bp/' component={BpSetLineup} />
          <Route path='/tagging-bp/' component={BpTagging} />
          <Route path='/review-bp/:id' component={BpSessionReview} />
          <Route path='/data-capture/' component={FieldCalibration} />
        </div>
        {showSyncBar() && <SyncBar />}
        {showScoreBar() && <ScoreBar />}
        {showBpScoreBar() && <BpScoreBar />}
        <FaqModal visible={uiStore.isFaqModalVisible} onDismissCallback={() => uiStore.setIsFaqModalVisible(false)} />
      </div>
    </Router>
  );
});

export default Layout;
